// Pure helpers for a latitude / longitude point. No React and no I/O.
//
// A point: { latitude, longitude } as numbers. The backend validates every
// coordinate again (range, pair); these exist so the browser never sends, links
// to, or displays a point that is not one.

// The same precision the server stores (DECIMAL(10, 7)).
const STORED_DECIMALS = 7;
const DECIMAL = /^[-+]?\d{1,3}(\.\d+)?$/;

function toNumber(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string" && DECIMAL.test(value.trim())) return Number(value.trim());
  return NaN;
}

const round = (value) => Math.round(value * 10 ** STORED_DECIMALS) / 10 ** STORED_DECIMALS;

// { latitude, longitude } from anything (numbers, strings, a MapLibre LngLat
// { lng, lat }), or null when it is not a valid point on Earth.
export function toPoint(input) {
  if (!input) return null;
  const latitude = toNumber(input.latitude ?? input.lat);
  const longitude = toNumber(input.longitude ?? input.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude: round(latitude), longitude: round(longitude) };
}

export const isValidPoint = (input) => toPoint(input) !== null;

// "19.28120" — five decimals (about 1 m) for reading.
export const formatCoordinate = (value, decimals = 5) => (Number.isFinite(value) ? value.toFixed(decimals) : "—");

// A point in a URL (?lat=..&lng=..), so "Create Journey" from the map survives a reload.
export function pointToSearch(point) {
  const valid = toPoint(point);
  return valid ? `lat=${valid.latitude}&lng=${valid.longitude}` : "";
}

// The point in a URL: { point } when the URL carries a valid one, { point: null,
// invalid: true } when it carries lat/lng that are not a valid point, or
// { point: null } when it carries none.
export function pointFromSearch(search) {
  const params = new URLSearchParams(search || "");
  if (!params.has("lat") && !params.has("lng")) return { point: null };
  const point = toPoint({ latitude: params.get("lat"), longitude: params.get("lng") });
  return point ? { point } : { point: null, invalid: true };
}

export const samePoint = (a, b) => Boolean(a && b && a.latitude === b.latitude && a.longitude === b.longitude);
